import { LoginBody } from 'api/modules/authentication'
import { screenSizes } from 'constants/global'
import { STAY_LOGGED_IN_STORAGE_ID } from 'constants/settings'
import { FormattedMessage, useIntl } from 'locales/index'
import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { RegisterForm } from 'shared/components/common/RegisterForm'
import { Button, Checkbox, CheckboxProps, Form, Input, Typography } from '@commutifi-fe/ui'
import localStorage from 'utils/storage'

export interface LoginFormProps {
  /**
   * Callback function when user logs in successfully
   */
  onLogin: (res: LoginBody) => void
  /**
   * Callback function when forgot password is clicked
   */
  onForgotPassword?: React.MouseEventHandler
  /**
   * To display any extra actions after the 'Sign In' button
   */
  extraActions?: React.ReactNode
  emailInputProps?: React.ComponentProps<typeof Input>
}

export function LoginForm({ onLogin, onForgotPassword, extraActions, emailInputProps }: LoginFormProps) {
  const intl = useIntl()
  const [form] = Form.useForm<LoginBody>()
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })

  const handleResetPassword: React.MouseEventHandler = (e) => onForgotPassword && onForgotPassword(e)
  const handleStayLoggedIn: CheckboxProps['onChange'] = (e) => {
    localStorage.put(STAY_LOGGED_IN_STORAGE_ID, e.target.checked)
  }

  const defaultStayLoggedIn = React.useMemo(
    () =>
      localStorage.get(STAY_LOGGED_IN_STORAGE_ID) ??
      Intl.DateTimeFormat().resolvedOptions().timeZone?.split('/')[0] !== 'Europe',
    []
  )

  return (
    <Form<LoginBody> form={form} layout="vertical" requiredMark={false} onFinish={onLogin}>
      {/* eslint-disable-next-line jsx-a11y/tabindex-no-positive -- Interaction is weird without tab index order */}
      <RegisterForm.EmailField tabIndex={1} {...emailInputProps} />
      <RegisterForm.PasswordField
        // eslint-disable-next-line jsx-a11y/tabindex-no-positive -- Interaction is weird without tab index order
        tabIndex={2}
        rules={[{ required: true, message: intl.formatMessage({ id: 'required' }), validateTrigger: 'onChange' }]}
        extra={
          <Button
            type="text"
            onClick={handleResetPassword}
            className="u-padding--none u-font-weight-semi u-margin-left--medium-2"
            style={{ minHeight: 'auto', whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'end' }}
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive -- Interaction is weird without tab index order
            tabIndex={4}
          >
            <FormattedMessage id="auth.login.forgotPassword" />
          </Button>
        }
      />
      <div className="u-flex-justify-end">
        <Checkbox
          // eslint-disable-next-line jsx-a11y/tabindex-no-positive -- Interaction is weird without tab index order
          tabIndex={3}
          onChange={handleStayLoggedIn}
          defaultChecked={defaultStayLoggedIn}
          className="ant-checkbox-rtl"
        >
          <Typography.Text type="secondary">
            <FormattedMessage id="auth.login.checkbox.stayLoggedIn" />
          </Typography.Text>
        </Checkbox>
      </div>
      <div className="u-flex-justify-end u-flex-align-start u-flex-wrap u-padding-top--small-2">
        <Form.Item shouldUpdate noStyle>
          {({ isFieldsTouched, getFieldsError }) => (
            <Button
              // eslint-disable-next-line jsx-a11y/tabindex-no-positive -- Interaction is weird without tab index order
              tabIndex={5}
              type="primary"
              htmlType="submit"
              className="u-margin-bottom--medium-2"
              style={{ minWidth: '100px' }}
              block={isXSmall}
              disabled={
                !isFieldsTouched(true) || Boolean(getFieldsError().filter(({ errors }) => errors.length).length)
              }
            >
              <FormattedMessage id="auth.login.submit" />
            </Button>
          )}
        </Form.Item>
        {extraActions}
      </div>
    </Form>
  )
}
